/* eslint-disable @typescript-eslint/naming-convention */
import { Component, OnInit } from '@angular/core';
import { ModalController, NavController, IonicModule } from '@ionic/angular';
import {
  Room,
  HotelSearchRequest,
} from 'src/app/models/hotel-booking/search-request';
import { ThemeService } from 'src/app/services/client/theme/theme.service';
import { HotelBookingService } from 'src/app/services/hotel-booking/hotel-booking.service';
import { SmartDatePipe } from 'src/app/pipes/smart-date.pipe';
import { environment } from 'src/environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { AnalyticsService } from 'src/app/services/analytics/analytics.service';
import { ActivatedRoute } from '@angular/router';
import { FlightHotelComponent } from 'src/app/modules/shared/modals/flight-hotel/flight-hotel.component';
import { AutoSuggestionModalComponent } from '../../booking/hotels/components/auto-suggestion-modal/auto-suggestion-modal.component';
import { RoomSelectionModalComponent } from '../../booking/hotels/components/room-selection-modal/room-selection-modal.component';
import { FlightBookingService } from 'src/app/services/flight-booking/flight-booking.service';
import { HelperService } from 'src/app/services/helper/helper.service';
@Component({
  selector: 'app-hotel-search',
  templateUrl: './hotel-search.component.html',
  styleUrls: ['./hotel-search.component.scss'],
})
export class HotelSearchComponent implements OnInit {
  public bannerList: any[];
  public currentHeaderData: any = {
    prevPageLink: '/home',
    title: 'hotel.hotel_search.header',
  };
  selectedSearchObject: any;
  checkInDate: any;
  checkOutDate: any;
  guestRoomsInfo: any[];
  starRating: string[];
  showValidationErrors: boolean;
  minDateAllowed: string;
  isSearchHotelModalOpen = false;
  defaultNationality: string;
  deals: any[];
  constructor(
    private modalController: ModalController,
    private navControl: NavController,
    private hotelBookingService: HotelBookingService,
    private smartDatePipe: SmartDatePipe,
    private translate: TranslateService,
    private analyticsService: AnalyticsService,
    private activatedRoute: ActivatedRoute,
    private themeService: ThemeService,
    private flightService: FlightBookingService,
    private helperService: HelperService
  ) {}
  ngOnInit(): void {
    this.getHotelDeals();
    // Set default nationality from partner configuration
    this.defaultNationality = this.themeService.getDefaultNationality?.iso2;

    let queryParams;
    if (this.activatedRoute.snapshot.params.query) {
      queryParams = JSON.parse(
        decodeURI(this.activatedRoute.snapshot.params.query)
      );
    }

    const storedSearchQuery =
      this.hotelBookingService.getSearchQuery() as HotelSearchRequest;

    if (storedSearchQuery && !this.helperService.isHotelDealRoute()) {
      this.deSerializeRequest(storedSearchQuery);
    } else if (queryParams) {
      this.selectedSearchObject = {
        entityId: queryParams?.entityId,
        label: queryParams?.title + ' , ' + queryParams?.country,
        type: queryParams?.type,
      };
      this.setDefaultDates();
    } else {
      this.setDefaultDates();
    }
  }

  ionViewDidEnter() {
    this.bannerList = null;
    this.showValidationErrors = false;
    const storedSearchQuery =
      this.hotelBookingService.getSearchQuery() as HotelSearchRequest;
    this.minDateAllowed = this.smartDatePipe.transform(
      new Date(),
      'yyyy-MM-dd',
      'en-US'
    );
    if (storedSearchQuery && !this.selectedSearchObject) {
      this.deSerializeRequest(storedSearchQuery);
    } else {
      if (!this.selectedSearchObject) {
        this.selectedSearchObject = {};
        this.setDefaultDates();
      }
    }
    this.hotelBookingService.getDealcarousel(244, 1).subscribe({
      next: (response) => {
        if (response?.status === 0 && response?.selectedTabItems) {
          this.bannerList = response?.selectedTabItems[0]?.banners;
        } else {
          this.bannerList = [];
        }
      },
      error: (err: any) => {
        this.bannerList = [];
      },
      complete: () => {},
    });
    this.showFirstTimeModalIfNeeded();
  }

  setDefaultDates() {
    const currentDate = new Date();
    const numberOfDaysToAdd = 1;
    this.guestRoomsInfo = [
      {
        adults: 1,
        children: 0,
        childrenAges: [],
      },
    ];
    this.checkInDate = this.smartDatePipe.transform(
      currentDate,
      'yyyy-MM-dd',
      'en-US'
    );
    this.checkOutDate = this.smartDatePipe.transform(
      currentDate.setDate(currentDate.getDate() + numberOfDaysToAdd),
      'yyyy-MM-dd',
      'en-US'
    );
  }

  serializeRequest() {
    return {
      checkin: this.checkInDate,
      checkout: this.checkOutDate,
      type: this.selectedSearchObject.type,
      entityId: this.selectedSearchObject.entityId,
      nationality: this.defaultNationality,
      entityLabel: this.selectedSearchObject.label,
      pid: environment.pid,
      rt: 'compact',
      rooms: this.guestRoomsInfo as Room[],
      fq: {
        df_long_star_rating: this.starRating || [],
      },
    } as HotelSearchRequest;
  }

  deSerializeRequest(storedSearchQuery) {
    const checkinDate = new Date(storedSearchQuery.checkin);
    const checkOutDate = new Date(storedSearchQuery.checkout);
    const today = new Date();
    if (
      checkinDate.getTime() > today.getTime() &&
      checkOutDate.getTime() > today.getTime()
    ) {
      this.checkInDate = storedSearchQuery.checkin;
      this.checkOutDate = storedSearchQuery.checkout;
    } else {
      this.setDefaultDates();
    }
    this.selectedSearchObject = {
      entityId: storedSearchQuery.entityId,
      label: storedSearchQuery.entityLabel,
      type: storedSearchQuery.type,
    };
    // Use default nationality from configuration instead of stored value
    this.defaultNationality = this.themeService.getDefaultNationality?.iso2;
    this.guestRoomsInfo = storedSearchQuery.rooms || [
      {
        adults: 1,
        children: 0,
        childrenAges: [],
      },
    ];
    this.starRating = storedSearchQuery.fq?.df_long_star_rating || [];
  }

  updateCheckoutDate() {
    this.analyticsService.trackUnified('date_updated', {
      flow: 'hotel',
      outcome: 'success',
      legacy_event_name: 'DateUpdate',
      checkinDate: this.checkInDate,
      checkoutDate: this.checkOutDate,
    });
    if (!this.checkInDate) {
      return;
    }

    const checkinDate = new Date(this.checkInDate);
    const today = new Date();

    // Reset time to start of day for accurate comparison
    today.setHours(0, 0, 0, 0);
    checkinDate.setHours(0, 0, 0, 0);

    // If check-in date is in the past, set it to today
    if (checkinDate.getTime() < today.getTime()) {
      this.checkInDate = this.smartDatePipe.transform(
        new Date(),
        'yyyy-MM-dd',
        'en-US'
      );
      checkinDate.setTime(today.getTime());
    }

    // If checkout date exists and is invalid, update it
    if (this.checkOutDate) {
      const checkOutDate = new Date(this.checkOutDate);
      checkOutDate.setHours(0, 0, 0, 0);

      if (checkOutDate.getTime() <= checkinDate.getTime()) {
        // Set checkout to next day after check-in
        const nextDay = new Date(checkinDate);
        nextDay.setDate(nextDay.getDate() + 1);
        this.checkOutDate = this.smartDatePipe.transform(
          nextDay,
          'yyyy-MM-dd',
          'en-US'
        );
      }
    } else {
      // If no checkout date, set it to next day after check-in
      const nextDay = new Date(checkinDate);
      nextDay.setDate(nextDay.getDate() + 1);
      this.checkOutDate = this.smartDatePipe.transform(
        nextDay,
        'yyyy-MM-dd',
        'en-US'
      );
    }
  }

  submitSearchQuery() {
    // Validate dates before submission
    if (!this.areDatesValid()) {
      this.showValidationErrors = true;
      return;
    }

    let requestObject;
    try {
      requestObject = this.serializeRequest();
    } catch (error) {
      this.showValidationErrors = true;
      return;
    }

    if (requestObject?.type) {
      this.analyticsService.trackUnified('search_started', {
        flow: 'hotel',
        step: 'search',
        step_index: 1,
        outcome: 'success',
        legacy_event_name: 'search',
        search: requestObject,
        button: 'search-button',
      });
      this.navControl.navigateForward([
        '/hotel/search-results',
        { query: encodeURI(JSON.stringify(requestObject)) },
      ]);
    } else {
      this.showValidationErrors = true;
    }
  }

  async presentCityHotelSearchModal() {
    if (this.isSearchHotelModalOpen) return;
    this.isSearchHotelModalOpen = true;
    this.analyticsService.trackUnified('search_input_clicked', {
      flow: 'hotel',
      outcome: 'success',
      legacy_event_name: 'Hotel Search Input',
      search: this.selectedSearchObject,
      button: 'search-input',
    });
    const modal = await this.modalController.create({
      component: AutoSuggestionModalComponent,
      backdropDismiss: false,
    });

    modal.onDidDismiss().then(async (data) => {
      if (data.data) {
        this.analyticsService.trackHotelCitySearch(data?.data);
        this.selectedSearchObject = data?.data;
        if (this.selectedSearchObject.type === 'REGION') {
          this.selectedSearchObject.label =
            this.selectedSearchObject.title +
            ' , ' +
            this.selectedSearchObject.city +
            ' , ' +
            this.selectedSearchObject.country;
        } else {
          this.selectedSearchObject.label =
            this.selectedSearchObject.title +
            ' , ' +
            this.selectedSearchObject.country;
        }
      }
      this.isSearchHotelModalOpen = false;
    });
    return await modal.present();
  }

  async presentRoomSelectionModal() {
    this.analyticsService.trackUnified('guest_room_selection_opened', {
      flow: 'hotel',
      outcome: 'success',
      legacy_event_name: 'Guest-Room Selection',
      button: 'room-selection',
    });
    const modal = await this.modalController.create({
      component: RoomSelectionModalComponent,
      backdropDismiss: false,
      componentProps: {
        guestRoomsInfo: this.guestRoomsInfo,
      },
    });
    modal.onDidDismiss().then(async (data) => {
      if (data.data) {
        this.analyticsService.trackUnified('guest_room_selection_saved', {
          flow: 'hotel',
          outcome: 'success',
          legacy_event_name: 'Selected Guest-Room',
          button: 'room-selection',
          guestRoomsInfo: data.data.guestRoomsInfo,
        });
        this.guestRoomsInfo = data.data.guestRoomsInfo;
      }
    });
    return await modal.present();
  }

  getRoomString() {
    if (this.guestRoomsInfo) {
      const roomCount = this.guestRoomsInfo?.length;
      let guestCount = 0;
      this.guestRoomsInfo.forEach((item) => {
        guestCount += item.adults + item.children;
      });

      const guestText =
        guestCount === 1
          ? this.translate.instant('hotel.hotel_search.guest')
          : this.translate.instant('hotel.hotel_search.guests');

      const roomText =
        roomCount === 1
          ? this.translate.instant('hotel.hotel_search.room')
          : this.translate.instant('hotel.hotel_search.rooms');

      // eslint-disable-next-line max-len
      return `${guestCount} ${guestText}, ${roomCount} ${roomText}`;
    }
    return '';
  }

  calculateMinDate() {
    if (!this.checkInDate) {
      return this.minDateAllowed;
    }
    const minDate = new Date(this.checkInDate);
    const nextDay = new Date(minDate);
    nextDay.setDate(nextDay.getDate() + 1);
    return this.smartDatePipe.transform(nextDay, 'yyyy-MM-dd', 'en-US');
  }

  /**
   * Validate and update checkout date when it changes
   */
  onCheckoutDateChange() {
    if (!this.checkOutDate || !this.checkInDate) {
      return;
    }

    const checkinDate = new Date(this.checkInDate);
    const checkOutDate = new Date(this.checkOutDate);
    const today = new Date();

    // Reset time to start of day for accurate comparison
    today.setHours(0, 0, 0, 0);
    checkinDate.setHours(0, 0, 0, 0);
    checkOutDate.setHours(0, 0, 0, 0);

    // If checkout date is before or same as check-in date, set it to next day
    if (checkOutDate.getTime() <= checkinDate.getTime()) {
      const nextDay = new Date(checkinDate);
      nextDay.setDate(nextDay.getDate() + 1);
      this.checkOutDate = this.smartDatePipe.transform(
        nextDay,
        'yyyy-MM-dd',
        'en-US'
      );
    }
  }

  /**
   * Check if all required fields are filled for hotel search
   * @returns boolean indicating if form is valid
   */
  get isFormValid(): boolean {
    return !!(
      this.selectedSearchObject?.entityId &&
      this.checkInDate &&
      this.checkOutDate &&
      this.guestRoomsInfo &&
      this.guestRoomsInfo.length > 0 &&
      this.guestRoomsInfo.every((room) => room.adults > 0) &&
      this.areDatesValid()
    );
  }

  /**
   * Validate that dates are not in the past and checkout is after checkin
   * @returns boolean indicating if dates are valid
   */
  areDatesValid(): boolean {
    if (!this.checkInDate || !this.checkOutDate) {
      return false;
    }

    const today = new Date();
    const checkinDate = new Date(this.checkInDate);
    const checkOutDate = new Date(this.checkOutDate);

    // Reset time to start of day for accurate comparison
    today.setHours(0, 0, 0, 0);
    checkinDate.setHours(0, 0, 0, 0);
    checkOutDate.setHours(0, 0, 0, 0);

    // Check if check-in date is not in the past
    if (checkinDate.getTime() < today.getTime()) {
      return false;
    }

    // Check if checkout date is after check-in date
    if (checkOutDate.getTime() <= checkinDate.getTime()) {
      return false;
    }

    return true;
  }
  async showFlightHotelModal() {
    try {
      const modal = await this.modalController.create({
        component: FlightHotelComponent,
        cssClass: 'login-success-modal',
        backdropDismiss: true,
        showBackdrop: true,
        componentProps: { variant: 'hotel' }
      });
      return await modal.present();
    } catch (error) {
      console.error('Error showing Flight Hotel Modal:', error);
    }
  }

  private async showFirstTimeModalIfNeeded() {
    try {
      // Check if modal has been shown before for hotels
      const hasShownHotelModal = localStorage.getItem(
        'flightHotelModal_shown_hotel'
      );
      const sessionKey = 'modal_shown_session_hotel';

      if (hasShownHotelModal || sessionStorage.getItem(sessionKey)) {
        return;
      }

      await this.showFlightHotelModal();

      // Mark as shown in both localStorage and sessionStorage
      localStorage.setItem('flightHotelModal_shown_hotel', 'true');
      sessionStorage.setItem(sessionKey, 'true');
    } catch (err) {
      console.error('Auto-show modal failed:', err);
    }
  }

  datePicker() {
    this.analyticsService.trackUnified('date_picker_opened', {
      flow: 'hotel',
      outcome: 'success',
      legacy_event_name: 'DatePicker',
      button: 'date-picker',
    });
  }

  starRatingChange() {
    this.analyticsService.trackUnified('star_rating_changed', {
      flow: 'hotel',
      outcome: 'success',
      legacy_event_name: 'Star Rating Change',
      starRating: this.starRating,
    });
  }

  goToAllDeals() {
    this.navControl.navigateForward(['/hotel-deals/home']);
  }

  getHotelDeals() {
    this.flightService.getCarousels('bfl-hotel-deals', 'page').subscribe((res: any) => {
        if (res?.tabs?.length > 0) {
          this.deals = res?.tabs;
        } else {
          this.deals = [];
        }
      });
  }

  onDealClick(deal: any) {
    this.analyticsService.trackUnified('hotel_deal_clicked', {
      flow: 'hotel',
      outcome: 'success',
      legacy_event_name: 'Hotel Deal Clicked',
      dealTitle: deal.title,
      dealSubtitle: deal.subTitle,
    });
    this.navControl.navigateForward('hotel-deals/home', {
      queryParams: {
        dealType: deal.type,
      },
    });
  }
}
